import { useQuery } from '@tanstack/react-query';
import { portalGet } from '@/lib/portal/customerClient';
import { usePortalAuthStore } from '@/stores/portalAuth.store';

export interface PortalCoveredService {
  name: string;
  quantityPerPeriod: number | null;
  entitlementPeriodType: string | null;
  kind: string | null;
}

export interface PortalMembership {
  discounts?: Array<{
    type: 'PERCENT' | 'FLAT' | 'SET_PRICE';
    value: number;
    scope: string;
    hasExclusions: boolean;
  }> | null;
  coveredEquipment?: Array<{
    id: string;
    name: string;
    make: string | null;
    model: string | null;
    serialNumber: string | null;
  }>;
  id: string;
  membershipNumber: string;
  status: string;
  planName: string | null;
  billingAmount: number;
  billingCurrency: string;
  billingIntervalValue: number;
  billingIntervalUnit: string;
  /** Date-only calendar values — render with a date-only formatter. */
  nextBillingDate: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  contractType: string;
  contractStartDate: string | null;
  contractEndDate: string | null;
  autoRenew: boolean;
  activatedAt: string | null;
  cancellationEffectiveDate: string | null;
  /** Given on a free, office-only option: the base is free (extra units may
   * still bill) and it ends on contractEndDate instead of renewing. */
  isComplimentary?: boolean;
  coveredServices: PortalCoveredService[];
  coveredVisits: { total: number; used: number; remaining: number } | null;
}

/** The signed-in contact's memberships (newest first). */
export function usePortalMemberships() {
  const token = usePortalAuthStore((s) => s.token);
  return useQuery({
    queryKey: ['portal', 'memberships', token],
    queryFn: () => portalGet<PortalMembership[]>('/api/portal/memberships'),
    enabled: Boolean(token),
    retry: false,
  });
}

/** One membership the contact owns; 404s (via ApiError) for anything else. */
export function usePortalMembership(id: string) {
  const token = usePortalAuthStore((s) => s.token);
  return useQuery({
    queryKey: ['portal', 'membership', id, token],
    queryFn: () => portalGet<PortalMembership>(`/api/portal/memberships/${encodeURIComponent(id)}`),
    enabled: Boolean(token && id),
    retry: false,
  });
}
